import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "../components/L";
import axios from "axios";
import { Calendar, Euro, ArrowRight, ChevronDown, Users, Award, BookOpen, GraduationCap, Send, ChevronLeft, ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useLanguage } from "../i18n/LanguageContext";
import { translateDate } from "../i18n/dateTranslate";
import useSEO from "../hooks/useSEO";
import { resolveAssetUrl } from "../utils/assets";
import TestimonialsSection from "../components/TestimonialsSection";
import useEmblaCarousel from "embla-carousel-react";
import { formatPrice } from "../utils/price";
import { getNextMasterclassModule, parseAlbanianDateRange } from "../data/masterclassModules";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Count-up animation hook
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) { setStarted(true); return; }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startOnView, started]);

  useEffect(() => {
    if (!started) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, end, duration]);

  return { count, ref };
};

const CountUpStat = ({ icon: Icon, end, suffix, label }) => {
  const { count, ref } = useCountUp(end, 2200);
  return (
    <div ref={ref} className="text-center">
      <Icon className="mx-auto mb-3 text-[#1CA3B0]" size={28} />
      <p className="text-2xl md:text-3xl font-black text-[#08707A] font-['Inter_Tight']">
        {count}{suffix}
      </p>
      <p className="text-sm text-[#828D9E] font-medium mt-1">{label}</p>
    </div>
  );
};

// Subscribe Form
const SubscribeForm = () => {
  const { tx } = useLanguage();
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [agreed, setAgreed] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !agreed) return;
    setStatus("sending");
    try {
      const names = form.name.trim().split(" ", 2);
      await axios.post(`${API}/leads`, {
        first_name: names[0] || "",
        last_name: names[1] || "",
        email: form.email.trim(),
        phone: form.phone.trim(),
        referral_source: "Subscribe Form",
        referral_detail: "Homepage Subscribe",
      });
      setStatus("success");
      setForm({ name: "", phone: "", email: "" });
    } catch {
      setStatus("success");
      setForm({ name: "", phone: "", email: "" });
    }
  };

  if (status === "success") {
    return (
      <div className="bg-white/[0.06] backdrop-blur-md border border-white/10 rounded-2xl p-8 text-center" data-testid="subscribe-success">
        <div className="w-14 h-14 rounded-full bg-[#08707A]/20 flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-check text-[#3CD4E1] text-xl"></i>
        </div>
        <h3 className="text-lg font-bold text-white mb-2">{tx("Faleminderit!")}</h3>
        <p className="text-white/60 text-sm">{tx("Regjistrimi juaj u krye me sukses. Do te njoftoheni per eventet e reja.")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white/[0.06] backdrop-blur-md border border-white/10 rounded-2xl p-8" data-testid="subscribe-form">
      <h3 className="text-lg font-bold text-white mb-1 font-['Inter_Tight']">{tx("Regjistrohu Tani")}</h3>
      <p className="text-white/50 text-xs mb-6">{tx("Vendos te dhenat e tua dhe behu pjese e eventeve tona.")}</p>
      <div className="space-y-3.5">
        <div className="relative">
          <i className="fas fa-user absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-xs"></i>
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={tx("Emri Mbiemri")} required className="w-full rounded-lg bg-white/[0.07] border border-white/10 pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#3CD4E1]/50 focus:ring-1 focus:ring-[#3CD4E1]/30 transition-colors" data-testid="subscribe-name" />
        </div>
        <div className="relative">
          <i className="fas fa-phone absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-xs"></i>
          <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={tx("Nr. Celular")} className="w-full rounded-lg bg-white/[0.07] border border-white/10 pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#3CD4E1]/50 focus:ring-1 focus:ring-[#3CD4E1]/30 transition-colors" data-testid="subscribe-phone" />
        </div>
        <div className="relative">
          <i className="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-xs"></i>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" required className="w-full rounded-lg bg-white/[0.07] border border-white/10 pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#3CD4E1]/50 focus:ring-1 focus:ring-[#3CD4E1]/30 transition-colors" data-testid="subscribe-email" />
        </div>
      </div>
      <label className="flex items-start gap-3 mt-5 cursor-pointer select-none" data-testid="subscribe-terms">
        <div className="relative mt-0.5 shrink-0">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="sr-only peer" />
          <div className="w-[18px] h-[18px] rounded border border-white/20 bg-white/[0.05] peer-checked:bg-[#08707A] peer-checked:border-[#3CD4E1] transition-colors flex items-center justify-center">
            {agreed && <i className="fas fa-check text-white text-[9px]"></i>}
          </div>
        </div>
        <span className="text-white/60 text-[11px] leading-relaxed">
          {tx("Une pranoj")} <a href="/kushtet" className="text-[#3CD4E1] hover:underline">{tx("Termat dhe Kushtet")}</a> {tx("dhe")} <a href="/privatesia" className="text-[#3CD4E1] hover:underline">{tx("Politika e Privatesise")}</a> {tx("ne Albanian Aligners Academy")}
        </span>
      </label>
      <button type="submit" disabled={status === "sending" || !agreed} className="mt-5 w-full rounded-lg py-3.5 text-sm font-bold text-white inline-flex items-center justify-center gap-3 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-40 disabled:hover:translate-y-0" style={{ background: "linear-gradient(130deg, #3CD4E1 0%, #08707A 100%)" }} data-testid="subscribe-submit">
        {status === "sending" ? (
          <span className="inline-flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> {tx("Duke derguar...")}</span>
        ) : (
          <>{tx("Regjistrohu")} <i className="fas fa-long-arrow-alt-right text-base"></i></>
        )}
      </button>
    </form>
  );
};

// ---------- Upcoming Events Card (portrait) + Slider ----------
const UpcomingEventCard = ({ event, testId }) => {
  const { tx } = useLanguage();
  return (

  <Link
    to={`/evente/${event.slug || event.id}`}
    className="bg-white border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-500 rounded-2xl overflow-hidden group flex flex-col"
    data-testid={testId}
  >
    <div className="relative overflow-hidden aspect-[3/4] bg-[#f0f4f5]">
      <img
        src={event.image_url || "https://images.pexels.com/photos/23625648/pexels-photo-23625648.jpeg"}
        alt={event.title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute top-3 left-3 bg-[#1CA3B0] text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm">
        {tx("E ardhshme")}
      </div>
    </div>
    <div className="p-5 flex flex-col flex-1">
      <h3 className="text-base font-bold tracking-tight mb-2 font-['Inter_Tight'] text-[#0A0D10] line-clamp-2 group-hover:text-[#1CA3B0] transition-colors">
        {event.title}
      </h3>
      <p className="text-xs text-[#4A5568] mb-3 leading-relaxed line-clamp-2">{event.description}</p>
      <div className="flex items-center justify-between pt-3 mt-auto border-t border-slate-100">
        <span className="flex items-center gap-1.5 text-xs text-[#828D9E]">
          <Calendar size={12} /> {event.date}
        </span>
        <span className="text-sm font-bold text-[#08707A]">
          {event.price ? formatPrice(event.price) : tx("Falas")}
        </span>
      </div>
      <span className="mt-3 text-xs font-semibold text-[#1CA3B0] inline-flex items-center gap-1.5">
        {tx("Detaje")} <i className="fas fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform"></i>
      </span>
    </div>
  </Link>
  );
};

const UpcomingEventsSection = ({ events }) => {
  const { tx } = useLanguage();
  const list = events.filter((e) => e.category === "upcoming" && e.id !== "masterclass-2026");
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", loop: false, slidesToScroll: 1 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setSnapCount(emblaApi.scrollSnapList().length);
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
  }, [emblaApi, onSelect]);

  if (list.length === 0) return null;

  const showSlider = list.length >= 2;

  return (
    <section className="py-20 md:py-28 px-6 md:px-12" data-testid="home-upcoming-events" style={{ background: "#ffffff" }}>
      <div className="max-w-[1350px] mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1CA3B0] mb-4">{tx("Te Ardhshme")}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-['Inter_Tight']">
              {tx("Eventet e ardhshme")}
            </h2>
          </div>
          <Link
            to="/evente"
            className="text-[#1CA3B0] font-semibold text-sm hover:text-[#08707A] transition-colors inline-flex items-center gap-1"
            data-testid="home-upcoming-view-all"
          >
            {tx("Shiko te gjitha")} <ArrowRight size={14} />
          </Link>
        </div>

        {!showSlider ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8 max-w-3xl">
            {list.map((ev, i) => (
              <UpcomingEventCard key={ev.id} event={ev} testId={`home-upcoming-card-${i}`} />
            ))}
          </div>
        ) : (
          <div className="relative" data-testid="home-upcoming-slider">
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex gap-6 md:gap-8">
                {list.map((ev, i) => (
                  <div
                    key={ev.id}
                    className="shrink-0 grow-0 basis-[85%] sm:basis-[60%] lg:basis-[55%]"
                  >
                    <UpcomingEventCard event={ev} testId={`home-upcoming-card-${i}`} />
                  </div>
                ))}
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-between mt-8">
              <div className="flex items-center gap-2" data-testid="home-upcoming-dots">
                {Array.from({ length: snapCount }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => emblaApi && emblaApi.scrollTo(i)}
                    aria-label={`Slide ${i + 1}`}
                    className={`h-2 rounded-full transition-all ${i === selectedIndex ? "w-8 bg-[#08707A]" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
                    data-testid={`home-upcoming-dot-${i}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => emblaApi && emblaApi.scrollPrev()}
                  disabled={!canPrev}
                  aria-label={tx("I meparshem")}
                  className="w-10 h-10 rounded-full border border-slate-300 text-[#08707A] hover:bg-[#08707A] hover:text-white hover:border-[#08707A] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#08707A] disabled:hover:border-slate-300 transition-all flex items-center justify-center"
                  data-testid="home-upcoming-prev"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => emblaApi && emblaApi.scrollNext()}
                  disabled={!canNext}
                  aria-label={tx("Tjeter")}
                  className="w-10 h-10 rounded-full border border-slate-300 text-[#08707A] hover:bg-[#08707A] hover:text-white hover:border-[#08707A] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#08707A] disabled:hover:border-slate-300 transition-all flex items-center justify-center"
                  data-testid="home-upcoming-next"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const HomePage = () => {
  const [events, setEvents] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const { t, tx, lang } = useLanguage();

  useSEO({
    title: tx("Kurse, Trajnime dhe Evente per Dentiste"),
    description: tx("Albanian Aligners Academy - Akademia e pare ne Shqiperi per kurse aligners, trajnime ortodoncie, workshop stomatologjik dhe masterclass per dentiste. Edukim profesional me eksperte nderkombetare."),
    keywords: "kurse aligners, trajnime per aligners, evente per dentiste, workshop stomatologjik, aligners academy, trajnim ortodoncie, kurse dentare ne Shqiperi, masterclass per dentiste",
    canonical: "https://aligners.academy",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, trainersRes, faqRes, blogRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/trainers`),
          axios.get(`${API}/faq`),
          axios.get(`${API}/blog`),
        ]);
        setEvents(eventsRes.data);
        setTrainers(trainersRes.data);
        setFaqs(faqRes.data);
        setBlogPosts(blogRes.data || []);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    fetchData();
  }, []);

  const upcomingEvents = events.filter((e) => e.category === "upcoming").slice(0, 3);

  // Countdown to the next masterclass module (auto-advances when a module ends)
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, mins: 0, secs: 0 });
  const [nextModule, setNextModule] = useState(() => getNextMasterclassModule());
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      // Re-pick the current/next module in case a module just ended between ticks.
      const mod = getNextMasterclassModule(now);
      setNextModule((prev) => (prev && mod && prev.id === mod.id ? prev : mod));
      if (!mod) {
        setCountdown({ days: 0, hours: 0, mins: 0, secs: 0 });
        return;
      }
      const range = mod._range || parseAlbanianDateRange(mod.date);
      // Countdown target = start of the module. If the module is already running, countdown to its end.
      const target = (range.start.getTime() > now.getTime() ? range.start : range.end).getTime();
      const diff = Math.max(0, target - now.getTime());
      setCountdown({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        mins: Math.floor((diff % 3600000) / 60000),
        secs: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section with Video Background */}
      <section className="relative flex items-center overflow-hidden" style={{ height: '820px' }} data-testid="hero-section">
        <style>{`
          @keyframes countPulse { 0%,100% { opacity:1; } 50% { opacity:0.6; } }
          @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
          @keyframes glowRing { 0%,100% { box-shadow: 0 0 20px rgba(60,212,225,0.1), 0 25px 70px rgba(0,0,0,0.5); } 50% { box-shadow: 0 0 40px rgba(60,212,225,0.25), 0 25px 70px rgba(0,0,0,0.5); } }
          @keyframes borderGlow { 0%,100% { border-color: rgba(60,212,225,0.08); } 50% { border-color: rgba(60,212,225,0.2); } }
          @keyframes floatUp { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
          @keyframes snakeSpin { 0% { transform: translate(-50%,-50%) rotate(0deg); } 100% { transform: translate(-50%,-50%) rotate(360deg); } }
          .snake-border {
            position: relative;
            border-radius: 14px;
            padding: 3px;
            overflow: hidden;
          }
          .snake-border::before {
            content: '';
            position: absolute;
            top: 50%; left: 50%;
            width: 200%; height: 200%;
            background: conic-gradient(from 0deg, transparent 0%, transparent 30%, #3CD4E1 45%, #1CA3B0 50%, #08707A 55%, transparent 70%, transparent 100%);
            animation: snakeSpin 3s linear infinite;
          }
          .snake-border::after {
            content: '';
            position: absolute;
            inset: 3px;
            border-radius: 12px;
            background: #0a0d14;
            z-index: 1;
          }
          .snake-border .snake-inner {
            position: relative;
            z-index: 2;
            border-radius: 12px;
            overflow: hidden;
          }
        `}</style>
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" poster="/api/files/local/hero-bg.png">
          <source src="/api/files/local/hero-video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(130deg, rgba(53, 65, 91, 0.8) 0%, rgba(0, 63, 114, 0.7) 100%)' }} />
        <div className="relative z-10 max-w-[1350px] mx-auto px-6 md:px-12 w-full pt-24 sm:pt-20 lg:pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-12 items-center">
            {/* Left: Original Text */}
            <div className="max-w-lg">
              <p className="text-sm uppercase tracking-[0.15em] text-white/70 mb-6 hidden md:flex items-center gap-3 animate-fade-up opacity-0 stagger-1" data-testid="hero-overline">
                {t("hero_welcome")} <strong className="text-white">{t("hero_brand")}</strong>
                <span className="h-px w-12 bg-white/40 inline-block"></span>
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white mb-6 animate-fade-up opacity-0 stagger-2" style={{ fontFamily: "'Noto Sans', sans-serif" }} data-testid="hero-title">
                {t("hero_title")}
              </h1>
              <p className="text-white/70 leading-relaxed mb-10 animate-fade-up opacity-0 stagger-3">
                {t("hero_desc")}
              </p>
              <div className="flex flex-wrap gap-4 animate-fade-up opacity-0 stagger-4">
                <Link
                  to="/rreth-nesh"
                  className="rounded-md px-6 py-3 font-semibold transition-all duration-300 hover:-translate-y-1 inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white/10"
                  data-testid="hero-about-btn"
                >
                  {t("hero_read_more")} <i className="fas fa-arrow-right text-sm"></i>
                </Link>
                <Link
                  to="/evente"
                  className="rounded-md px-6 py-3 font-semibold transition-all duration-300 hover:-translate-y-1 inline-flex items-center gap-2 text-white shadow-lg"
                  style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #1CA3B0 100%)' }}
                  data-testid="hero-events-btn"
                >
                  <i className="fas fa-calendar-alt"></i> {t("hero_view_events")}
                </Link>
              </div>
            </div>

            {/* Tablet: Poster + Info (only 640-1023px) */}
            <div className="hidden sm:flex lg:hidden flex-row items-start gap-4 mt-6 animate-fade-up opacity-0 stagger-3 max-w-[500px]">
              <Link to="/evente/masterclass" className="block relative group flex-shrink-0" data-testid="hero-poster-link-mobile">
                <div className="relative rounded-xl overflow-hidden w-[180px] sm:w-[160px]">
                  <img
                    src="/api/files/local/masterclass-poster-alt.png"
                    alt="Modern Implantology Masterclass"
                    className="w-full h-auto"
                  />
                </div>
              </Link>
              <div className="text-center sm:text-left">
                <span className="px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider bg-white/10 text-white/70 border border-white/15">{t('hero_masterclass_6modules')}</span>
                <h3 className="text-white font-bold text-base mt-2">Modern Implantology Masterclass</h3>
                <p className="text-white/60 text-xs mt-1">From Fundamentals to Full-Arch Excellence</p>
                <div className="flex justify-center sm:justify-start gap-2 mt-3" data-testid="hero-countdown-mobile">
                  {[
                    { val: countdown.days, label: t('hero_countdown_days') },
                    { val: countdown.hours, label: t('hero_countdown_hours') },
                    { val: countdown.mins, label: t('hero_countdown_min') },
                    { val: countdown.secs, label: t('hero_countdown_sec'), accent: true },
                  ].map((item, i) => (
                    <div key={i} className="text-center">
                      <div className={`rounded-lg px-2.5 py-1.5 ${item.accent ? 'bg-[#3CD4E1]/10 border border-[#3CD4E1]/25' : 'bg-white/[0.05] border border-white/[0.08]'}`}>
                        <span className={`text-base font-black font-['Inter_Tight'] ${item.accent ? 'text-[#3CD4E1]' : 'text-white'}`}>{String(item.val).padStart(2, '0')}</span>
                      </div>
                      <p className="text-[7px] uppercase tracking-wider text-white/30 mt-1">{item.label}</p>
                    </div>
                  ))}
                </div>
                <Link
                  to="/evente/masterclass"
                  className="inline-flex items-center gap-2 mt-3 rounded-lg px-5 py-2 text-xs font-bold text-white transition-all"
                  style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #08707A 100%)' }}
                  data-testid="hero-masterclass-btn-mobile"
                >
                  {t('hero_view_more')} <i className="fas fa-long-arrow-alt-right"></i>
                </Link>
              </div>
            </div>

            {/* Phone only: poster left + info right */}
            <div className="flex sm:hidden flex-row items-center gap-3 mt-4 animate-fade-up opacity-0 stagger-3">
              <Link to="/evente/masterclass" className="block flex-shrink-0" data-testid="hero-poster-link-phone">
                <div className="rounded-xl overflow-hidden w-[120px]">
                  <img src="/api/files/local/masterclass-poster-alt.png" alt="Masterclass" className="w-full h-auto" />
                </div>
              </Link>
              <div className="text-left flex-1 min-w-0">
                <span className="px-2 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider bg-white/10 text-white/70 border border-white/15">{t('hero_masterclass_6modules')}</span>
                <h3 className="text-white font-bold text-sm mt-1.5 leading-tight">Modern Implantology Masterclass</h3>
                <p className="text-white/50 text-[10px] mt-0.5">From Fundamentals to Full-Arch Excellence</p>
                <div className="flex gap-1.5 mt-2">
                  {[
                    { val: countdown.days, label: t('hero_countdown_days') },
                    { val: countdown.hours, label: t('hero_countdown_hours') },
                    { val: countdown.mins, label: t('hero_countdown_min') },
                    { val: countdown.secs, label: t('hero_countdown_sec'), accent: true },
                  ].map((item, i) => (
                    <div key={i} className="text-center">
                      <div className={`rounded px-1.5 py-1 ${item.accent ? 'bg-[#3CD4E1]/10 border border-[#3CD4E1]/25' : 'bg-white/[0.05] border border-white/[0.08]'}`}>
                        <span className={`text-sm font-black font-['Inter_Tight'] ${item.accent ? 'text-[#3CD4E1]' : 'text-white'}`}>{String(item.val).padStart(2, '0')}</span>
                      </div>
                      <p className="text-[6px] uppercase tracking-wider text-white/30 mt-0.5">{item.label}</p>
                    </div>
                  ))}
                </div>
                <Link to="/evente/masterclass" className="inline-flex items-center gap-1.5 mt-2 rounded-lg px-3 py-1.5 text-[10px] font-bold text-white" style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #08707A 100%)' }}>
                  {t('hero_view_more')} <i className="fas fa-arrow-right text-[8px]"></i>
                </Link>
              </div>
            </div>

            {/* Right: Poster + Countdown Showcase */}
            <div className="hidden lg:flex items-center gap-5 animate-fade-up opacity-0 stagger-3">
              {/* Poster */}
              <Link to="/evente/masterclass" className="block relative group shrink-0" data-testid="hero-poster-link">
                <div className="snake-border p-[3px]" style={{ width: '346px', overflow: 'hidden', animation: 'floatUp 6s ease-in-out infinite' }}>
                  <div className="snake-inner rounded-xl overflow-hidden">
                  <img
                    src="/api/files/local/masterclass-poster-alt.png"
                    alt="Modern Implantology Masterclass"
                    className="w-full h-auto group-hover:scale-[1.03] transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#08707A]/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-end justify-center pb-5">
                    <span className="text-white text-xs font-bold inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/15 backdrop-blur-md border border-white/25 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                      {tx("Shiko Modulet")} <i className="fas fa-arrow-right"></i>
                    </span>
                  </div>
                  </div>
                </div>
              </Link>

              {/* Info Column */}
              <div className="flex flex-col gap-3 pb-1" style={{ width: '200px' }}>
                {/* Badges */}
                <div className="flex flex-wrap gap-1.5">
                  <span className="px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider bg-white/5 text-white/70 border border-white/15">{t('hero_masterclass_6modules')}</span>
                </div>

                {/* Title */}
                <div>
                  <h3 className="text-white font-bold text-base font-['Inter_Tight'] leading-tight">Modern Implantology Masterclass</h3>
                  <p className="text-white/60 text-[11px] mt-1">From Fundamentals to Full-Arch Excellence</p>
                </div>

                {/* Divider */}
                <div className="h-px bg-gradient-to-r from-[#3CD4E1]/20 via-white/10 to-transparent" />

                {/* Countdown */}
                <div>
                  <p className="text-[8px] uppercase tracking-[0.15em] text-white/30 mb-2">{t('hero_starts_in')}</p>
                  <div className="grid grid-cols-4 gap-1.5" data-testid="hero-countdown">
                    {[
                      { val: countdown.days, label: t('hero_countdown_days') },
                      { val: countdown.hours, label: t('hero_countdown_hours') },
                      { val: countdown.mins, label: t('hero_countdown_min') },
                      { val: countdown.secs, label: t('hero_countdown_sec'), accent: true },
                    ].map((item, i) => (
                      <div key={i} className="text-center">
                        <div className={`rounded-lg py-2 ${item.accent ? 'bg-[#3CD4E1]/10 border border-[#3CD4E1]/25' : 'bg-white/[0.05] border border-white/[0.08]'}`} style={item.accent ? { animation: 'borderGlow 2s ease-in-out infinite' } : {}}>
                          <span className={`text-lg font-black font-['Inter_Tight'] ${item.accent ? 'text-[#3CD4E1]' : 'text-white'}`} style={item.accent ? { animation: 'countPulse 1s ease-in-out infinite' } : {}}>{String(item.val).padStart(2, '0')}</span>
                        </div>
                        <p className="text-[7px] uppercase tracking-wider text-white/30 mt-1">{item.label}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-white mt-2" data-testid="hero-next-date"><i className="fas fa-calendar-alt text-[#3CD4E1]/50 mr-1"></i>{nextModule ? `${t('hero_next_module_prefix')} ${nextModule.id} - ${translateDate(nextModule.date, lang)}` : t('hero_masterclass_finished')}
                  </p>
                </div>

                {/* Divider */}
                <div className="h-px bg-gradient-to-r from-[#3CD4E1]/20 via-white/10 to-transparent" />

                {/* CTA */}
                <Link
                  to="/evente/masterclass"
                  className="rounded-lg py-2.5 text-xs font-bold text-white flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#3CD4E1]/25"
                  style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #08707A 100%)' }}
                  data-testid="hero-masterclass-btn"
                >
                  {t('hero_view_more')} <i className="fas fa-long-arrow-alt-right"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="bg-white border-b border-slate-100" data-testid="stats-section">
        <div className="max-w-[1350px] mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          <CountUpStat icon={Users} end={1250} suffix="+" label={t("stat_participants")} />
          <CountUpStat icon={Award} end={20} suffix="+" label={t("stat_certifications")} />
          <CountUpStat icon={BookOpen} end={15} suffix="+" label={t("stat_events")} />
          <CountUpStat icon={GraduationCap} end={20} suffix="" label={t("stat_trainers")} />
        </div>
      </section>

      {/* About Preview */}
      <section className="py-20 md:py-28 px-6 md:px-12" data-testid="about-preview" style={{ background: 'rgba(244, 249, 252, 0.5)' }}>
        <div className="max-w-[1350px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-sm uppercase tracking-[0.15em] mb-4 flex items-center gap-3" style={{ color: '#1CA3B0' }}>
              {t("about_who")} <span className="h-px w-12 inline-block" style={{ backgroundColor: 'rgba(28,163,176,0.3)' }}></span>
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6" style={{ fontFamily: "'Noto Sans', sans-serif" }}>
              {t("about_title")}
            </h2>
            <p className="text-[#4A5568] mb-4">
              {t("about_p1")}
            </p>
            <p className="text-[#4A5568] mb-8">
              <strong className="text-[#0A0D10]">Albanian Aligners Academy</strong> {t("about_p2")}
            </p>
            <Link
              to="/rreth-nesh"
              className="rounded-md px-6 py-3 font-semibold transition-all duration-300 hover:-translate-y-1 inline-flex items-center gap-2 text-white"
              style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)' }}
              data-testid="about-preview-btn"
            >
              {t("hero_read_more")} <i className="fas fa-arrow-right text-sm"></i>
            </Link>
          </div>
          <div className="relative">
            <div className="overflow-hidden shadow-xl" style={{ borderRadius: '10px' }}>
              <img
                src="/api/files/local/about-team.jpg"
                alt="Albanian Aligners Academy Workshop"
                className="w-full h-[500px] object-cover object-center"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events — Masterclass Modules as Cards */}
      <section className="relative py-20 md:py-32 px-6 md:px-12 overflow-hidden" data-testid="events-section">
        <style>{`
          @keyframes eventsBgShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(130deg, #0a3a5c 0%, #003F72 25%, #02263d 50%, #003F72 75%, #0a3a5c 100%)',
            backgroundSize: '300% 300%',
            animation: 'eventsBgShift 14s ease infinite',
          }}
        />
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: 'radial-gradient(circle at 20% 20%, rgba(60,212,225,0.15), transparent 55%), radial-gradient(circle at 80% 80%, rgba(28,163,176,0.18), transparent 55%)',
          }}
        />
        <div className="relative z-10 max-w-[1350px] mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#3CD4E1] mb-4">{t("events_label")}</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-['Inter_Tight'] text-white">
                {t("events_upcoming")}
              </h2>
            </div>
            <Link
              to="/evente"
              className="mt-4 md:mt-0 text-[#3CD4E1] font-semibold text-sm hover:text-white transition-colors inline-flex items-center gap-1"
              data-testid="view-all-events-link"
            >
              {t("events_view_all")} <ArrowRight size={14} />
            </Link>
          </div>

          {/* Main Masterclass Feature Card */}
          <Link to="/evente/masterclass" className="block group" data-testid="masterclass-feature-home">
            <div className="bg-white border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-500 rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] lg:grid-cols-[380px_1fr]">
                <div className="relative overflow-hidden">
                  <img
                    src="/api/files/local/masterclass-poster-alt.png"
                    alt="Modern Implantology Masterclass"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ minHeight: '280px' }}
                  />
                  <div className="absolute top-4 left-4 bg-[#003F72] text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider">
                    Masterclass
                  </div>
                </div>
                <div className="p-6 md:p-8 flex flex-col justify-center">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className="text-[11px] font-bold text-white bg-[#1CA3B0] px-3 py-1 rounded-full">{t('hero_masterclass_6modules')}</span>
                    <span className="text-[11px] font-bold text-[#003F72] bg-[#003F72]/10 px-3 py-1 rounded-full">{t('events_masterclass_dates')}</span>
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight mb-2 font-['Inter_Tight'] text-[#0A0D10] group-hover:text-[#1CA3B0] transition-colors">
                    {t('events_masterclass_title')}
                  </h3>
                  <p className="text-sm italic text-[#1CA3B0] mb-3">{t('events_masterclass_sub')}</p>
                  <p className="text-sm text-[#4A5568] mb-5 leading-relaxed">{t('home_masterclass_desc')}</p>
                  <div className="inline-flex items-center gap-2 text-sm font-bold text-white bg-[#003F72] hover:bg-[#1CA3B0] px-5 py-2.5 rounded-lg transition-all w-fit group-hover:bg-[#1CA3B0]">
                    {t('hero_read_more')} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Upcoming Standard Events */}
      <UpcomingEventsSection events={events} />

      {/* Trainers Preview */}
      <section className="relative py-20 md:py-32" data-testid="trainers-preview" style={{ background: 'rgba(244, 249, 252, 0.5)' }}>
        <style>{`
          @keyframes homeTrainerHaloSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes homeTrainerGlow { 0%,100% { opacity: 0.55; } 50% { opacity: 1; } }
          .home-trainer-card {
            transition: transform 0.55s cubic-bezier(0.22,1,0.36,1),
                        box-shadow 0.55s cubic-bezier(0.22,1,0.36,1),
                        border-color 0.4s ease;
            will-change: transform;
          }
          .home-trainer-card:hover {
            transform: translateY(-10px) scale(1.015);
            box-shadow: 0 28px 60px -20px rgba(8, 112, 122, 0.30),
                        0 12px 30px -12px rgba(0, 63, 114, 0.15);
            border-color: rgba(28, 163, 176, 0.35);
          }
          .home-trainer-card .halo {
            position: absolute;
            inset: -3px;
            border-radius: 9999px;
            background: conic-gradient(from 0deg, #3CD4E1, #1CA3B0, #003F72, #1CA3B0, #3CD4E1);
            opacity: 0;
            transition: opacity 0.5s ease;
            filter: blur(2px);
          }
          .home-trainer-card:hover .halo {
            opacity: 1;
            animation: homeTrainerHaloSpin 6s linear infinite, homeTrainerGlow 2.4s ease-in-out infinite;
          }
          .home-trainer-card .photo {
            transition: transform 0.9s cubic-bezier(0.22,1,0.36,1), filter 0.5s ease;
          }
          .home-trainer-card:hover .photo {
            transform: scale(1.12);
            filter: saturate(1.1) contrast(1.03);
          }
          .home-trainer-card .name {
            transition: color 0.35s ease;
          }
          .home-trainer-card:hover .name { color: #1CA3B0; }
          .home-trainer-card .cta-arrow {
            transition: transform 0.35s ease, letter-spacing 0.35s ease;
          }
          .home-trainer-card:hover .cta-arrow {
            transform: translateX(6px);
            letter-spacing: 0.02em;
          }
        `}</style>
        <div className="px-6 md:px-12 max-w-[1350px] mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1CA3B0] mb-4">{t("trainers_label")}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-['Inter_Tight']">
              {t("trainers_title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {trainers.slice(0, 4).map((trainer, i) => {
              const imgSrc = resolveAssetUrl(trainer.image_url, "https://images.pexels.com/photos/23625642/pexels-photo-23625642.jpeg");
              return (
                <Link
                  key={trainer.id}
                  to={`/trajnere/${trainer.slug || trainer.id}`}
                  className="relative bg-white border border-slate-200 rounded-3xl overflow-hidden text-center block home-trainer-card"
                  data-testid={`trainer-preview-${i}`}
                >
                  <div className="pt-10 pb-5">
                    <div className="relative w-36 h-36 md:w-40 md:h-40 mx-auto">
                      <div className="halo" />
                      <div className="relative w-full h-full rounded-full overflow-hidden border-[5px] border-white shadow-[0_8px_30px_rgba(8,112,122,0.18)]">
                        <img src={imgSrc} alt={trainer.name} className="w-full h-full object-cover photo" />
                      </div>
                    </div>
                  </div>
                  <div className="px-5 pb-7">
                    <h3 className="text-lg font-bold tracking-tight mb-1.5 name" style={{ color: 'rgb(28,163,176)', fontFamily: "'Noto Sans', sans-serif" }}>
                      {trainer.name}
                    </h3>
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#08707A] mb-3 inline-block px-2.5 py-1 rounded-full" style={{ background: 'rgba(28,163,176,0.08)' }}>
                      {trainer.specialty}
                    </p>
                    <span className="block text-xs font-semibold text-[#1CA3B0] cta-arrow">
                      {tx("Shiko Profilin →")}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-16">
            <Link
              to="/trajnere"
              className="rounded-full px-8 py-3 font-semibold transition-all duration-300 hover:-translate-y-1 inline-flex items-center gap-2 bg-transparent border-2 border-[#08707A] text-[#08707A] hover:bg-[#08707A]/5"
              data-testid="view-all-trainers-btn"
            >
              {t("trainers_view_all")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials (before Blog) */}
      <TestimonialsSection variant="light" testId="home-testimonials" />

      {/* Blog Preview */}
      {blogPosts.length > 0 && (
        <section className="py-20 md:py-32 px-6 md:px-12" data-testid="blog-preview" style={{ background: '#ffffff' }}>
          <style>{`
            .home-blog-card {
              transition: transform 0.55s cubic-bezier(0.22,1,0.36,1),
                          box-shadow 0.55s cubic-bezier(0.22,1,0.36,1);
              will-change: transform;
            }
            .home-blog-card:hover {
              transform: translateY(-8px);
              box-shadow: 0 24px 50px -18px rgba(8,112,122,0.28);
            }
            .home-blog-card .cover {
              transition: transform 0.9s cubic-bezier(0.22,1,0.36,1);
            }
            .home-blog-card:hover .cover { transform: scale(1.08); }
            .home-blog-card .title {
              transition: color 0.35s ease;
            }
            .home-blog-card:hover .title { color: #1CA3B0; }
            .home-blog-card .read-arrow {
              transition: transform 0.35s ease;
            }
            .home-blog-card:hover .read-arrow { transform: translateX(6px); }
          `}</style>
          <div className="max-w-[1350px] mx-auto">
            <div className="mb-14">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-['Inter_Tight'] mb-6 text-left">
                Blog
              </h2>
              <p className="text-[#4A5568] leading-relaxed text-base">
                {tx("Zbuloni teknikat, protokollet dhe strategjitë klinike më moderne të përfshira në programet tona trajnuese, të ndërtuara për t'ju ndihmuar të arrini rezultate të avancuara dhe të parashikueshme në trajtimet me aligners. Qëndroni të përditësuar me risitë, biomekanikën dhe teknologjitë më të fundit në ortodoncinë digjitale.")}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.filter(p => p.published !== false).slice(0, 3).map((post, i) => {
                const cover = resolveAssetUrl(post.cover_image, "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=800");
                let dateStr = "";
                if (post.created_at) {
                  const d = new Date(post.created_at);
                  dateStr = `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
                }
                return (
                  <Link
                    key={post.id}
                    to={`/blog/${post.id}`}
                    className="bg-white border border-slate-200 rounded-2xl overflow-hidden home-blog-card block"
                    data-testid={`blog-card-${i}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                      <img src={cover} alt={post.title} className="w-full h-full object-cover cover" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2.5 mb-3 text-[10px] text-[#828D9E]">
                        <span className="font-bold uppercase tracking-[0.15em] text-[#1CA3B0] text-[9px]">{post.category || "Blog"}</span>
                        {dateStr && <><span className="text-[#CBD5E0]">•</span><span>{dateStr}</span></>}
                      </div>
                      <h3 className="text-lg font-bold tracking-tight text-[#0A0D10] mb-2 title leading-snug line-clamp-2" style={{ fontFamily: "'Noto Sans', sans-serif" }}>
                        {post.title}
                      </h3>
                      {post.excerpt && (
                        <p className="text-sm text-[#4A5568] leading-relaxed mb-4 line-clamp-3">{post.excerpt}</p>
                      )}
                      <span className="text-xs font-semibold text-[#1CA3B0] inline-flex items-center gap-1.5">
                        {t('hero_read_more')} <i className="fas fa-arrow-right text-[10px] read-arrow"></i>
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="text-center mt-14">
              <Link
                to="/blog"
                className="rounded-full px-8 py-3 font-semibold transition-all duration-300 hover:-translate-y-1 inline-flex items-center gap-2 bg-transparent border-2 border-[#08707A] text-[#08707A] hover:bg-[#08707A]/5"
                data-testid="view-all-blog-btn"
              >
                {tx("Shiko të gjitha postimet")} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Subscribe Section */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="subscribe-section">
        <style>{`
          @keyframes bgShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(270deg, #0A0D14, #141B2D, #1A1F35, #0F1420, #0A0D14)', backgroundSize: '400% 400%', animation: 'bgShift 20s ease infinite' }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="relative z-10 max-w-[1350px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#3CD4E1] mb-4">{tx("Regjistrohu")}</p>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-6 font-['Inter_Tight'] leading-tight">
                {tx("Qendro i Informuar per Eventet Ekskluzive te Aligners Academy")}
              </h2>
              <p className="text-white/70 leading-relaxed mb-8">
                {tx("Bashkohu me komunitetin e stomatologeve qe duan te zhvillojne ekspertizen e tyre ne ortodoncine dixhitale dhe trajtimet me aligner.")}
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#08707A]/20 flex items-center justify-center mt-0.5 shrink-0"><i className="fas fa-check text-[9px] text-[#3CD4E1]"></i></div>
                  <p className="text-white text-sm">{tx("Ftesa per workshop-e dhe konferenca")}</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#08707A]/20 flex items-center justify-center mt-0.5 shrink-0"><i className="fas fa-check text-[9px] text-[#3CD4E1]"></i></div>
                  <p className="text-white text-sm">{tx("Njoftime per evente te reja")}</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#08707A]/20 flex items-center justify-center mt-0.5 shrink-0"><i className="fas fa-check text-[9px] text-[#3CD4E1]"></i></div>
                  <p className="text-white text-sm">{tx("Akses prioritare per regjistrim")}</p>
                </div>
              </div>
            </div>
            <div>
              <SubscribeForm />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-32 px-6 md:px-12 max-w-[1350px] mx-auto" data-testid="faq-section">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-20 items-start">
          {/* Left — Title & description */}
          <div className="lg:sticky lg:top-28">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1CA3B0] mb-4">{t("faq_label")}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-['Inter_Tight'] mb-6">
              {tx("Pyetjet më të shpeshta")}
            </h2>
            <p className="text-[#4A5568] leading-relaxed text-base">
              {tx("Kemi përmbledhur përgjigjet për disa nga pyetjet më të shpeshta që na drejtohen në platformën Albanian Aligner Academy. Nëse nuk gjeni përgjigjen që kërkoni, mos hezitoni të na kontaktoni direkt – jemi këtu për t'ju ndihmuar!")}
            </p>
            <Link
              to="/kontakt"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#1CA3B0] hover:gap-3 transition-all"
              data-testid="faq-contact-link"
            >
              {tx("Na kontaktoni")} <ArrowRight size={16} />
            </Link>
          </div>

          {/* Right — Accordion */}
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={faq.id}
                value={`faq-${i}`}
                className="border border-slate-200 rounded-xl px-6 data-[state=open]:shadow-md transition-shadow bg-white"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left font-semibold text-[#0A0D10] hover:text-[#08707A] transition-colors py-5 text-sm md:text-base">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#4A5568] text-sm leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
